import { RemoteRefreshTypeNameFilter } from '../../extractRefresh/models/filters/RemoteRefreshScheduleTypeNameFilter';
import { IRemoteRefreshScheduleInfo } from '../../extractRefresh/models/IRemoteRefreshScheduleInfo';
import { ListResult } from '../fetchers/ListResult';
import { IResourceQuery, ResourceQuery } from '../resources/ResourceQuery';
import { ILeafFilterClause } from '../ServerApi/types/IFilterClause';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { LocalSorter } from './LocalSorter';

/**
 * Wraps the `getSlice` method of an IResourceQuery to inject client-side sorting. All other methods are delegated to
 * the wrapped IResourceQuery. Performing client-side sorting in this manner means that fetching more than one page will
 * result in broken sorting. Ideally, this logic would exist in the backend as it does for every other content type.
 */
export class LocallySortingScheduleResourceQuery implements IResourceQuery<IRemoteRefreshScheduleInfo> {
  private localSorter: LocalSorter<IRemoteRefreshScheduleInfo>;

  constructor(private wrappedQuery: ResourceQuery<IRemoteRefreshScheduleInfo>) {
    this.localSorter = new LocalSorter<IRemoteRefreshScheduleInfo>();

    // Sort by the localized version of these fields rather than the raw values:
    this.localSorter.addTransformForValueComparison('refreshType', RemoteRefreshTypeNameFilter);
  }

  getSlice(start: number, size: number): Promise<ListResult<IRemoteRefreshScheduleInfo>> {
    return this.wrappedQuery.getSlice(start, size).then(listResult =>
      new ListResult(this.localSorter.sortByClauses(listResult.items, this.wrappedQuery.getOrder())));
  }

  getCount(): Promise<number> {
    return this.wrappedQuery.getCount();
  }

  getExtraParams(): any {
    return this.wrappedQuery.getExtraParams();
  }

  getFilters(): ILeafFilterClause[] {
    return this.wrappedQuery.getFilters();
  }

  getFirst(): Promise<IRemoteRefreshScheduleInfo> {
    return this.wrappedQuery.getFirst();
  }

  getOrder(): IOrderClause[] {
    return this.wrappedQuery.getOrder();
  }

  addFilter(filterClause: ILeafFilterClause): IResourceQuery<IRemoteRefreshScheduleInfo> {
    this.wrappedQuery = this.wrappedQuery.addFilter(filterClause);
    return this;
  }

  withExtraParams(extraParams?: any): IResourceQuery<IRemoteRefreshScheduleInfo> {
    this.wrappedQuery = this.wrappedQuery.withExtraParams(extraParams);
    return this;
  }

  withFilters(filterClauses: ILeafFilterClause[]): IResourceQuery<IRemoteRefreshScheduleInfo> {
    this.wrappedQuery = this.wrappedQuery.withFilters(filterClauses);
    return this;
  }

  withOrder(order: IOrderClause[]): IResourceQuery<IRemoteRefreshScheduleInfo> {
    this.wrappedQuery = this.wrappedQuery.withOrder(order);
    return this;
  }

  withFiltersAndOrder(filterClauses: ILeafFilterClause[], order: IOrderClause[]) {
    this.wrappedQuery = this.wrappedQuery.withFiltersAndOrder(filterClauses, order);
    return this;
  }
}
